/* eslint-disable @nx/enforce-module-boundaries */
import { ROUTES } from '@aibees/elephant/router';
import { Icon, VideoModal } from '@aibees/shared/components';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { useIsPeopleLookalikeEnabled } from '@aibees/elephant/hooks/people';
import { FeatureFlags } from '@aibees/elephant/types';
import { useFeatureFlagsContext } from '@aibees/shared/feature-flags';
import {
  useBoolean,
  useMount,
  useQueryStringState,
} from '@aibees/shared/hooks';
import { classnames } from '@aibees/shared/utils';
import { Button, Stack } from '@mui/material';
import { memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ServiceCardDialog } from '../service-card-dialog';
import * as Styled from './styles';
import * as SX from './sx';
import type { ServiceCardFooterProps as Props } from './types';

export const ServiceCardFooter = memo(({ className, service }: Props) => {
  const { isFeatureEnabled } = useFeatureFlagsContext();
  const isPeopleLookalikeEnabled = useIsPeopleLookalikeEnabled();
  const { params } = useQueryStringState();
  // @maziar todo: we need to take care of handling feature flags for people lookalike search in one place to avoid this logic
  const comingSoon = !(service.feature === FeatureFlags.PEOPLE_LOOKALIKE_SEARCH
    ? isPeopleLookalikeEnabled
    : isFeatureEnabled(service.feature));

  const { actionType } = service;

  const navigate = useNavigate();

  const dialogState = useBoolean();
  const videoState = useBoolean();

  const handleButtonClick = () => {
    if (actionType === 'DIALOG') {
      dialogState.onTrue();
      return;
    }
    if (actionType === 'ROUTE' && service.route) {
      const path = ROUTES[service.route];
      navigate(service.search ? `${path}${service.search}` : path);
    }
  };

  useMount(() => {
    if (params && Object.keys(params).length > 0) {
      if (
        params.type &&
        params.type === service.serviceType &&
        !dialogState.value
      ) {
        dialogState.onTrue();
      }
    } else {
      dialogState.onFalse();
    }
  });

  return (
    <Styled.Wrapper
      className={classnames('ServiceCardFooter', className)}
      sx={SX.root}
      direction="row"
      gap={1}
      justifyContent="space-between"
    >
      <ServiceCardDialog state={dialogState} service={service} />
      <Stack direction="row" gap={1}>
        <Button
          variant="outlined"
          onClick={() => {}}
          size="small"
          disabled
          startIcon={<Icon name="footstep" color="grey.400" />}
        />
        <Button
          variant="outlined"
          onClick={videoState.onTrue}
          size="small"
          disabled={!service.howItWorksVideoUrl}
          startIcon={<Icon name="video-camera" />}
        >
          2 Min
        </Button>
      </Stack>
      <Button
        variant="outlined"
        size="small"
        color="primary"
        onClick={handleButtonClick}
        disabled={comingSoon}
      >
        Try Now
      </Button>
      {service.howItWorksVideoUrl && (
        <VideoModal
          state={videoState}
          videoUrl={service.howItWorksVideoUrl}
          title={
            service.title ? `${service.title} — How it works` : 'How it works'
          }
        />
      )}
    </Styled.Wrapper>
  );
});

ServiceCardFooter.displayName = 'ServiceCardFooter';
